import { defineMessages } from "react-intl";

/**
 * Frontend-owned copy for the creation flow: chrome, validation and errors.
 *
 * The config's own keys (question labels, options, hints, placeholders) are
 * declared in `configMessages.ts` — split so this file stays readable while that
 * one grows to a few hundred entries.
 */
export default defineMessages({
  "taskCreation.cta.continue": {
    id: "taskCreation.cta.continue",
    defaultMessage: "Continue",
  },
  "taskCreation.cta.back": {
    id: "taskCreation.cta.back",
    defaultMessage: "Back",
  },
  "taskCreation.cta.getOffers": {
    id: "taskCreation.cta.getOffers",
    defaultMessage: "Get offers",
  },
  /** Editing an existing request: every screen saves in place, none publishes. */
  "taskCreation.cta.saveAndContinue": {
    id: "taskCreation.cta.saveAndContinue",
    defaultMessage: "Save and continue",
  },

  /**
   * Header step indicator ("STEP 1 OF 3"). Kept in natural case here and
   * capitalised with CSS `textTransform="uppercase"` where it's rendered —
   * baking caps into the copy itself would fight case-sensitive locales.
   */
  "taskCreation.header.stepIndicator": {
    id: "taskCreation.header.stepIndicator",
    defaultMessage: "Step {step} of {total}",
  },

  /* Step names, used by the progress header and the summary rows. */
  "taskCreation.step.service_details": {
    id: "taskCreation.step.service_details",
    defaultMessage: "Service details",
  },
  "taskCreation.step.time_and_location": {
    id: "taskCreation.step.time_and_location",
    defaultMessage: "Time & location",
  },
  "taskCreation.step.contact_details": {
    id: "taskCreation.step.contact_details",
    defaultMessage: "Contact details",
  },
  "taskCreation.step.status.completed": {
    id: "taskCreation.step.status.completed",
    defaultMessage: "Completed",
  },
  "taskCreation.step.status.inProgress": {
    id: "taskCreation.step.status.inProgress",
    defaultMessage: "In progress",
  },
  "taskCreation.step.status.pending": {
    id: "taskCreation.step.status.pending",
    defaultMessage: "Pending",
  },

  /* Assistant prefill. */
  "taskCreation.prefill.banner.title": {
    id: "taskCreation.prefill.banner.title",
    defaultMessage: "Auto-filled from your description",
  },
  "taskCreation.prefill.banner.body": {
    id: "taskCreation.prefill.banner.body",
    defaultMessage:
      "We used the details from your description to fill this in. Please double-check that everything is correct.",
  },
  "taskCreation.prefill.banner.dismiss": {
    id: "taskCreation.prefill.banner.dismiss",
    defaultMessage: "Dismiss",
  },

  /* Discard guard. */
  "taskCreation.discardGuard.title": {
    id: "taskCreation.discardGuard.title",
    defaultMessage: "Go back and start over?",
  },
  "taskCreation.discardGuard.body": {
    id: "taskCreation.discardGuard.body",
    defaultMessage: "The answers you've already given will be lost.",
  },
  "taskCreation.discardGuard.confirm": {
    id: "taskCreation.discardGuard.confirm",
    defaultMessage: "Discard and start over",
  },
  "taskCreation.discardGuard.cancel": {
    id: "taskCreation.discardGuard.cancel",
    defaultMessage: "Keep my answers",
  },

  /* Exit guard: a click heading out of the flow (RT-4321). */
  "taskCreationExitModal.title": {
    id: "taskCreationExitModal.title",
    defaultMessage: "Leaving already ?",
  },
  "taskCreationExitModal.body": {
    id: "taskCreationExitModal.body",
    defaultMessage:
      "That's okay, we'll save a draft so you can continue your request later. But you're only a few steps away from reaching your <b>service provider</b>. Are you sure you want to leave ?",
  },
  "taskCreationExitModal.continueButton": {
    id: "taskCreationExitModal.continueButton",
    defaultMessage: "Continue my request",
  },
  "taskCreationExitModal.quitButton": {
    id: "taskCreationExitModal.quitButton",
    defaultMessage: "Save request and quit",
  },

  /* Resume. */
  "taskCreation.resume.title": {
    id: "taskCreation.resume.title",
    defaultMessage: "We saved this request. Do you still need help with it?",
  },
  "taskCreation.resume.continue": {
    id: "taskCreation.resume.continue",
    defaultMessage: "Yes, continue my request",
  },
  "taskCreation.resume.discard": {
    id: "taskCreation.resume.discard",
    defaultMessage: "No, discard and start over",
  },
  "taskCreation.summary.title": {
    id: "taskCreation.summary.title",
    defaultMessage: "Your request so far",
  },
  /* "Step 1: Service details" — the number is the step's position in the
     flow, which the config decides, so it is interpolated rather than baked
     into three separate strings. */
  "taskCreation.summary.stepLabel": {
    id: "taskCreation.summary.stepLabel",
    defaultMessage: "Step {number}: {name}",
  },
  "taskCreation.summary.draftSaved": {
    id: "taskCreation.summary.draftSaved",
    defaultMessage: "Draft saved",
  },
  "taskCreation.summary.close": {
    id: "taskCreation.summary.close",
    defaultMessage: "Close",
  },

  /* Empty state of the date chip, which draws its own text instead of letting
     the native input draw this — see `DateQuestion`'s pill variant. Each locale
     gets the letters its own browsers use ("jj/mm/aaaa" in French), in the
     field order it reads dates in. */
  "taskCreation.date.placeholder": {
    id: "taskCreation.date.placeholder",
    defaultMessage: "dd/mm/yyyy",
  },

  /* Validation. */
  "taskCreation.validation.required": {
    id: "taskCreation.validation.required",
    defaultMessage: "This answer is required",
  },
  "taskCreation.validation.selectAtLeastOne": {
    id: "taskCreation.validation.selectAtLeastOne",
    defaultMessage: "Select at least one option",
  },
  "taskCreation.validation.maxLength": {
    id: "taskCreation.validation.maxLength",
    defaultMessage: "Please enter less than {max} characters",
  },
  "taskCreation.validation.minLength": {
    id: "taskCreation.validation.minLength",
    defaultMessage: "Please enter at least {min} characters",
  },
  "taskCreation.validation.notANumber": {
    id: "taskCreation.validation.notANumber",
    defaultMessage: "Enter a number",
  },
  "taskCreation.validation.wholeNumber": {
    id: "taskCreation.validation.wholeNumber",
    defaultMessage: "Only positive and whole numbers allowed",
  },
  "taskCreation.validation.minimum": {
    id: "taskCreation.validation.minimum",
    defaultMessage: "Only positive numbers allowed",
  },
  "taskCreation.validation.dateInPast": {
    id: "taskCreation.validation.dateInPast",
    defaultMessage: "The date cannot be in the past",
  },
  "taskCreation.validation.invalidDate": {
    id: "taskCreation.validation.invalidDate",
    defaultMessage: "Pick a date",
  },
  "taskCreation.validation.addressRequired": {
    id: "taskCreation.validation.addressRequired",
    defaultMessage: "Pick an address from the suggestions",
  },
  /* `ContactInfoModal` — one wording whichever rule tripped. */
  "taskCreation.ContactInfoModal.title": {
    id: "taskCreation.ContactInfoModal.title",
    defaultMessage: "Let's protect your personal information",
  },
  "taskCreation.ContactInfoModal.body": {
    id: "taskCreation.ContactInfoModal.body",
    defaultMessage:
      "This field is public: avoid sharing your contact details here (address, phone, name, urls). They'll be requested in a dedicated field, kept protected and only accessible to the provider you select.",
  },
  "taskCreation.ContactInfoModal.editButton": {
    id: "taskCreation.ContactInfoModal.editButton",
    defaultMessage: "Edit my text",
  },
  /* The one answer to every way the address can fall short — typed but never
     picked, or picked and resolving to less than a doorstep. Naming the parts
     it wants beats naming the parts it did not get: the user has to re-pick
     from the list either way, and the list is what the sentence points at. */
  "taskCreation.validation.addressIncomplete": {
    id: "taskCreation.validation.addressIncomplete",
    defaultMessage:
      "Please enter a complete address (street, number, postal code, city) and select it from the suggested list.",
  },

  /* The flow's provisional end — see the TODO in `QuestionScreen`. */

  /* Errors. */
  "taskCreation.error.draftGone": {
    id: "taskCreation.error.draftGone",
    defaultMessage: "Your draft could not be found. Please start over.",
  },
  "taskCreation.error.saveFailed": {
    id: "taskCreation.error.saveFailed",
    defaultMessage: "We could not save your answer. Please try again.",
  },
  "taskCreation.error.descriptionRejected": {
    id: "taskCreation.error.descriptionRejected",
    defaultMessage:
      "We cannot accept this description. Please rewrite it and try again.",
  },

  /* Subcategory picker (RT-4321 M9). */
  "taskCreation.subcategory.heading": {
    id: "taskCreation.subcategory.heading",
    defaultMessage: "Choose a sub-category",
  },
  // The `<h1>` is not the radio group's label, so the group needs its own —
  // visually hidden, since the heading already says it on screen.

  /* Description step (RT-4321). */
  "taskCreation.description.heading": {
    id: "taskCreation.description.heading",
    defaultMessage: "Get offers from top <b>{category}</b> near you!",
  },
  // Used whenever no s_category name is available: the SEM pages send one,
  // the category picker does not, and it can also be loading, failed, or an
  // id that doesn't resolve in the tree. The bold half moves into the
  // generic words so the sentence keeps its shape either way.
  "taskCreation.description.heading.fallback": {
    id: "taskCreation.description.heading.fallback",
    defaultMessage: "Get offers from <b>service providers</b> near you!",
  },
  // Map pin prices per sub-category (RT-4506), from the product sheet. Keyed
  // on `S_CATEGORY_KEY_BY_ID`; price4 is a placeholder for desktop's 4th pin.
  // Any other sub-category (e.g. a SEM page outside the list): the Figma mock's.
  "taskCreation.description.pins.default.price1": {
    id: "taskCreation.description.pins.default.price1",
    defaultMessage: "€20",
  },
  "taskCreation.description.pins.default.price2": {
    id: "taskCreation.description.pins.default.price2",
    defaultMessage: "€25",
  },
  "taskCreation.description.pins.default.price3": {
    id: "taskCreation.description.pins.default.price3",
    defaultMessage: "€22",
  },
  "taskCreation.description.pins.default.price4": {
    id: "taskCreation.description.pins.default.price4",
    defaultMessage: "€18",
  },
  "taskCreation.description.pins.painter.price1": {
    id: "taskCreation.description.pins.painter.price1",
    defaultMessage: "€22",
  },
  "taskCreation.description.pins.painter.price2": {
    id: "taskCreation.description.pins.painter.price2",
    defaultMessage: "€35",
  },
  "taskCreation.description.pins.painter.price3": {
    id: "taskCreation.description.pins.painter.price3",
    defaultMessage: "€25",
  },
  "taskCreation.description.pins.painter.price4": {
    id: "taskCreation.description.pins.painter.price4",
    defaultMessage: "€27",
  },
  "taskCreation.description.pins.electrician.price1": {
    id: "taskCreation.description.pins.electrician.price1",
    defaultMessage: "€25",
  },
  "taskCreation.description.pins.electrician.price2": {
    id: "taskCreation.description.pins.electrician.price2",
    defaultMessage: "€40",
  },
  "taskCreation.description.pins.electrician.price3": {
    id: "taskCreation.description.pins.electrician.price3",
    defaultMessage: "€29",
  },
  "taskCreation.description.pins.electrician.price4": {
    id: "taskCreation.description.pins.electrician.price4",
    defaultMessage: "€31",
  },
  "taskCreation.description.pins.carpenter.price1": {
    id: "taskCreation.description.pins.carpenter.price1",
    defaultMessage: "€25",
  },
  "taskCreation.description.pins.carpenter.price2": {
    id: "taskCreation.description.pins.carpenter.price2",
    defaultMessage: "€44",
  },
  "taskCreation.description.pins.carpenter.price3": {
    id: "taskCreation.description.pins.carpenter.price3",
    defaultMessage: "€30",
  },
  "taskCreation.description.pins.carpenter.price4": {
    id: "taskCreation.description.pins.carpenter.price4",
    defaultMessage: "€33",
  },
  "taskCreation.description.pins.plumber.price1": {
    id: "taskCreation.description.pins.plumber.price1",
    defaultMessage: "€25",
  },
  "taskCreation.description.pins.plumber.price2": {
    id: "taskCreation.description.pins.plumber.price2",
    defaultMessage: "€40",
  },
  "taskCreation.description.pins.plumber.price3": {
    id: "taskCreation.description.pins.plumber.price3",
    defaultMessage: "€29",
  },
  "taskCreation.description.pins.plumber.price4": {
    id: "taskCreation.description.pins.plumber.price4",
    defaultMessage: "€31",
  },
  "taskCreation.description.pins.appliance_repair.price1": {
    id: "taskCreation.description.pins.appliance_repair.price1",
    defaultMessage: "€30",
  },
  "taskCreation.description.pins.appliance_repair.price2": {
    id: "taskCreation.description.pins.appliance_repair.price2",
    defaultMessage: "€44",
  },
  "taskCreation.description.pins.appliance_repair.price3": {
    id: "taskCreation.description.pins.appliance_repair.price3",
    defaultMessage: "€35",
  },
  "taskCreation.description.pins.appliance_repair.price4": {
    id: "taskCreation.description.pins.appliance_repair.price4",
    defaultMessage: "€36",
  },
  "taskCreation.description.pins.handyman.price1": {
    id: "taskCreation.description.pins.handyman.price1",
    defaultMessage: "€30",
  },
  "taskCreation.description.pins.handyman.price2": {
    id: "taskCreation.description.pins.handyman.price2",
    defaultMessage: "€44",
  },
  "taskCreation.description.pins.handyman.price3": {
    id: "taskCreation.description.pins.handyman.price3",
    defaultMessage: "€35",
  },
  "taskCreation.description.pins.handyman.price4": {
    id: "taskCreation.description.pins.handyman.price4",
    defaultMessage: "€36",
  },
  "taskCreation.description.pins.locksmith.price1": {
    id: "taskCreation.description.pins.locksmith.price1",
    defaultMessage: "€29",
  },
  "taskCreation.description.pins.locksmith.price2": {
    id: "taskCreation.description.pins.locksmith.price2",
    defaultMessage: "€45",
  },
  "taskCreation.description.pins.locksmith.price3": {
    id: "taskCreation.description.pins.locksmith.price3",
    defaultMessage: "€29",
  },
  "taskCreation.description.pins.locksmith.price4": {
    id: "taskCreation.description.pins.locksmith.price4",
    defaultMessage: "€34",
  },
  "taskCreation.description.pins.bike_repair.price1": {
    id: "taskCreation.description.pins.bike_repair.price1",
    defaultMessage: "€20",
  },
  "taskCreation.description.pins.bike_repair.price2": {
    id: "taskCreation.description.pins.bike_repair.price2",
    defaultMessage: "€35",
  },
  "taskCreation.description.pins.bike_repair.price3": {
    id: "taskCreation.description.pins.bike_repair.price3",
    defaultMessage: "€29",
  },
  "taskCreation.description.pins.bike_repair.price4": {
    id: "taskCreation.description.pins.bike_repair.price4",
    defaultMessage: "€28",
  },
  "taskCreation.description.subheading": {
    id: "taskCreation.description.subheading",
    defaultMessage:
      "Describe what you need done. We'll instantly notify matching experts nearby and send their best offers directly to you.",
  },
  /* Title section, edit mode only. */
  "taskCreation.title.heading": {
    id: "taskCreation.title.heading",
    defaultMessage: "Title for the request",
  },
  "js_validate.required": {
    id: "js_validate.required",
    defaultMessage: "This field is required.",
  },
  "taskCreation.title.subheading": {
    id: "taskCreation.title.subheading",
    defaultMessage:
      "Try a title that reads as a quick introduction to the situation you need help with.",
  },
  // Static aria-label for the textarea — must never be replaced by the
  // animated placeholder string.
  "taskCreation.description.label": {
    id: "taskCreation.description.label",
    defaultMessage: "Describe what you need",
  },
  /* Dictation (RT-4340). */
  "taskCreation.dictation.start": {
    id: "taskCreation.dictation.start",
    defaultMessage: "Dictate your description",
  },
  "taskCreation.dictation.stop": {
    id: "taskCreation.dictation.stop",
    defaultMessage: "Stop",
  },
  // Shown in the pill that replaces the mic chip while the browser listens.
  "taskCreation.dictation.listening": {
    id: "taskCreation.dictation.listening",
    defaultMessage: "Listening ...",
  },
  "taskCreation.dictation.error.notAllowed": {
    id: "taskCreation.dictation.error.notAllowed",
    defaultMessage:
      "Microphone access is blocked. Allow it in your browser settings to dictate.",
  },
  "taskCreation.dictation.error.generic": {
    id: "taskCreation.dictation.error.generic",
    defaultMessage: "Dictation stopped unexpectedly. Please try again.",
  },
  "taskCreation.description.publicNotice": {
    id: "taskCreation.description.publicNotice",
    defaultMessage:
      "🔒 This description will be visible publicly. Please do not include personal info like your exact address or phone number.",
  },
  // Sample copy only — final placeholder wording arrives via a separate
  // copy brief and will replace these.
  "taskCreation.description.placeholder.generic1": {
    id: "taskCreation.description.placeholder.generic1",
    defaultMessage:
      "e.g., The pipe under my bathroom sink is leaking continuously. I need someone to replace or repair it.",
  },
  "taskCreation.description.placeholder.generic2": {
    id: "taskCreation.description.placeholder.generic2",
    defaultMessage:
      "e.g., I'd like the walls and ceiling of my 20 m² living room repainted in white.",
  },
  "taskCreation.description.placeholder.generic3": {
    id: "taskCreation.description.placeholder.generic3",
    defaultMessage:
      "e.g., Two power sockets in my bedroom stopped working and I need an electrician to check them.",
  },

  /**
   * The category-specific set the generic trio above stands in for: three
   * worked examples per trade, so someone who arrived with a category in hand
   * is not shown another trade's example. `getPlaceholderMessages` picks
   * between the two.
   */
  "taskCreation.painter.description.placeholder1": {
    id: "taskCreation.painter.description.placeholder1",
    defaultMessage:
      "e.g., My bedroom walls are light blue, and I'd like an off-white. The surface is in good condition.",
  },
  "taskCreation.painter.description.placeholder2": {
    id: "taskCreation.painter.description.placeholder2",
    defaultMessage:
      "e.g., The paint on my front door is peeling. It needs sanding and repainting.",
  },
  "taskCreation.painter.description.placeholder3": {
    id: "taskCreation.painter.description.placeholder3",
    defaultMessage:
      "e.g., The façade has greyed over time and shows signs of damp. I'm looking for a quote.",
  },
  "taskCreation.electrician.description.placeholder1": {
    id: "taskCreation.electrician.description.placeholder1",
    defaultMessage:
      "e.g., I'd like to install a pendant light in the living room, with no existing fixing point.",
  },
  "taskCreation.electrician.description.placeholder2": {
    id: "taskCreation.electrician.description.placeholder2",
    defaultMessage:
      "e.g., I need an extra socket near the desk. The wall is plasterboard.",
  },
  "taskCreation.electrician.description.placeholder3": {
    id: "taskCreation.electrician.description.placeholder3",
    defaultMessage:
      "e.g., One of my rooms has had no power since last night. The breaker isn't tripping.",
  },
  "taskCreation.carpenter.description.placeholder1": {
    id: "taskCreation.carpenter.description.placeholder1",
    defaultMessage:
      "e.g., My bedroom door rubs against the floor and doesn't close properly. It may need planing.",
  },
  "taskCreation.carpenter.description.placeholder2": {
    id: "taskCreation.carpenter.description.placeholder2",
    defaultMessage:
      "e.g., I'm looking for a custom-made wardrobe for an 8m² room. No precise plan yet.",
  },
  "taskCreation.carpenter.description.placeholder3": {
    id: "taskCreation.carpenter.description.placeholder3",
    defaultMessage:
      "e.g., The living room parquet is scratched in places after years of use. I'd like to have it sanded and varnished.",
  },
  "taskCreation.plumber.description.placeholder1": {
    id: "taskCreation.plumber.description.placeholder1",
    defaultMessage:
      "e.g., The pipe under the bathroom sink is leaking constantly. It needs repairing or replacing.",
  },
  "taskCreation.plumber.description.placeholder2": {
    id: "taskCreation.plumber.description.placeholder2",
    defaultMessage:
      "e.g., The bathroom tap is old and leaks at the mixer. I'd rather replace it.",
  },
  "taskCreation.plumber.description.placeholder3": {
    id: "taskCreation.plumber.description.placeholder3",
    defaultMessage:
      "e.g., The shower drain empties very slowly. I've already tried a drain cleaner.",
  },
  "taskCreation.applianceRepairer.description.placeholder1": {
    id: "taskCreation.applianceRepairer.description.placeholder1",
    defaultMessage:
      "e.g., My washing machine won't spin anymore. The laundry stays soaked at the end of the cycle.",
  },
  "taskCreation.applianceRepairer.description.placeholder2": {
    id: "taskCreation.applianceRepairer.description.placeholder2",
    defaultMessage:
      "e.g., My fridge hasn't been cooling properly for two days. I hear an unusual noise.",
  },
  "taskCreation.applianceRepairer.description.placeholder3": {
    id: "taskCreation.applianceRepairer.description.placeholder3",
    defaultMessage:
      "e.g., My electric oven isn't heating anymore, even at max temperature. The heating element seems to be working.",
  },
  "taskCreation.handyman.description.placeholder1": {
    id: "taskCreation.handyman.description.placeholder1",
    defaultMessage:
      "e.g., I have a storage unit to assemble, still in its box. I don't have the right tools.",
  },
  "taskCreation.handyman.description.placeholder2": {
    id: "taskCreation.handyman.description.placeholder2",
    defaultMessage:
      "e.g., A cupboard door handle broke recently. It needs replacing.",
  },
  "taskCreation.handyman.description.placeholder3": {
    id: "taskCreation.handyman.description.placeholder3",
    defaultMessage:
      "e.g., I'd like to mount a TV on the living room wall, which is concrete. No bracket yet.",
  },
  "taskCreation.lockRepairer.description.placeholder1": {
    id: "taskCreation.lockRepairer.description.placeholder1",
    defaultMessage:
      "e.g., I'm locked out. My key snapped in the lock and I need a quick response.",
  },
  "taskCreation.lockRepairer.description.placeholder2": {
    id: "taskCreation.lockRepairer.description.placeholder2",
    defaultMessage:
      "e.g., The cylinder on my front door is worn, the key struggles to turn. I want to replace it.",
  },
  "taskCreation.lockRepairer.description.placeholder3": {
    id: "taskCreation.lockRepairer.description.placeholder3",
    defaultMessage:
      "e.g., My door handle spins freely without engaging the inner mechanism.",
  },
  "taskCreation.bicycleRepairer.description.placeholder1": {
    id: "taskCreation.bicycleRepairer.description.placeholder1",
    defaultMessage:
      "e.g., My rear tyre is flat and the inner tube looks damaged. I want to replace both.",
  },
  "taskCreation.bicycleRepairer.description.placeholder2": {
    id: "taskCreation.bicycleRepairer.description.placeholder2",
    defaultMessage:
      "e.g., My bike brakes aren't gripping enough anymore, especially at the back. Worn pads?",
  },
  "taskCreation.bicycleRepairer.description.placeholder3": {
    id: "taskCreation.bicycleRepairer.description.placeholder3",
    defaultMessage:
      "e.g., My chain often slips when changing gears, especially uphill. Does the derailleur need adjusting?",
  },

  /* Address screen (RT-4321). The screen's heading and subtitle are keyed from
     the config and live in `config/messages.ts`; these two are pure chrome
     that no config key points at, so they belong here. */
  "taskCreation.address.placeholder": {
    id: "taskCreation.address.placeholder",
    defaultMessage: "Groenstraat 33, 9000, Gent, BEL",
  },
  "taskCreation.address.placeholder.fr": {
    id: "taskCreation.address.placeholder.fr",
    defaultMessage: "12 Rue de Rivoli, 75004, Paris, FRA",
  },
  "taskCreation.address.privacyNote": {
    id: "taskCreation.address.privacyNote",
    defaultMessage:
      "Your address is never public. It is only shared with the provider you choose.",
  },

  /*
   * The photos uploader (RT-4321).
   *
   * `addButton` is the label of both add-photo buttons — the photos screen's
   * full-width one and the description screen's toolbar chip. One wording, so
   * one key: the button says the same thing wherever it is, and Figma's
   * empty/non-empty split ("Add your first photo" / "Add more photos") is
   * deliberately not implemented.
   */
  "taskCreation.photos.addButton": {
    id: "taskCreation.photos.addButton",
    defaultMessage: "Add photos",
  },
  "taskCreation.photos.descriptionPlaceholder": {
    id: "taskCreation.photos.descriptionPlaceholder",
    defaultMessage: "Tap here to describe the picture",
  },
  "taskCreation.photos.descriptionLabel": {
    id: "taskCreation.photos.descriptionLabel",
    defaultMessage: "Description",
  },
  "taskCreation.photos.thumbnailAlt": {
    id: "taskCreation.photos.thumbnailAlt",
    defaultMessage: "Uploaded photo",
  },
  "taskCreation.photos.removeButton": {
    id: "taskCreation.photos.removeButton",
    defaultMessage: "Remove photo",
  },
  "taskCreation.photos.uploadError": {
    id: "taskCreation.photos.uploadError",
    defaultMessage: "We couldn't upload one of your photos. Please try again.",
  },
  "taskCreation.photos.removeError": {
    id: "taskCreation.photos.removeError",
    defaultMessage: "We couldn't remove this photo. Please try again.",
  },

  /*
   * What the uploader refused, and why (RT-4380).
   *
   * Three wordings per rule: the plain one when the selection was dropped
   * whole, and a `.partial` pair when some of it got through — `.one` for a
   * single survivor, `.other` for several. No ICU `plural` block: the app keeps
   * that branch in the code and ships one flat string per form (same shape as
   * `week.one` / `week.other`), which is what the translators work with.
   *
   * "Picture(s)" is deliberate — one toast covers however many files broke the
   * same rule, so the copy cannot commit to a number.
   */
  "taskCreation.photos.error.unsupportedFormat": {
    id: "taskCreation.photos.error.unsupportedFormat",
    defaultMessage: "Picture(s) with unsupported format were not uploaded",
  },
  "taskCreation.photos.error.unsupportedFormat.partial.one": {
    id: "taskCreation.photos.error.unsupportedFormat.partial.one",
    defaultMessage:
      "Picture(s) with unsupported format were not uploaded (Only 1 was added)",
  },
  "taskCreation.photos.error.unsupportedFormat.partial.other": {
    id: "taskCreation.photos.error.unsupportedFormat.partial.other",
    defaultMessage:
      "Picture(s) with unsupported format were not uploaded (Only {count} were added)",
  },
  "taskCreation.photos.error.tooLarge": {
    id: "taskCreation.photos.error.tooLarge",
    defaultMessage: "Picture(s) too large in size were not uploaded",
  },
  "taskCreation.photos.error.tooLarge.partial.one": {
    id: "taskCreation.photos.error.tooLarge.partial.one",
    defaultMessage:
      "Picture(s) too large in size were not uploaded. (Only 1 was added)",
  },
  "taskCreation.photos.error.tooLarge.partial.other": {
    id: "taskCreation.photos.error.tooLarge.partial.other",
    defaultMessage:
      "Picture(s) too large in size were not uploaded. (Only {count} were added)",
  },
  "taskCreation.photos.error.limitReached": {
    id: "taskCreation.photos.error.limitReached",
    defaultMessage: "The {max} photo limit has been reached.",
  },
  "taskCreation.photos.error.limitReached.partial.one": {
    id: "taskCreation.photos.error.limitReached.partial.one",
    defaultMessage:
      "The {max} photo limit has been reached. (Only 1 was added)",
  },
  "taskCreation.photos.error.limitReached.partial.other": {
    id: "taskCreation.photos.error.limitReached.partial.other",
    defaultMessage:
      "The {max} photo limit has been reached. (Only {count} were added)",
  },

  /* Email screen. */
  "taskCreation.auth.email.title": {
    id: "taskCreation.auth.email.title",
    defaultMessage: "Log in or create an account",
  },
  "taskCreation.auth.email.subtitle": {
    id: "taskCreation.auth.email.subtitle",
    defaultMessage: "To receive offers within 2 hours!",
  },
  "taskCreation.auth.email.label": {
    id: "taskCreation.auth.email.label",
    defaultMessage: "E-mail address",
  },
  "taskCreation.auth.email.placeholder": {
    id: "taskCreation.auth.email.placeholder",
    defaultMessage: "eric.c@myemail.com",
  },
  "taskCreation.auth.email.invalid": {
    id: "taskCreation.auth.email.invalid",
    defaultMessage: "Enter a valid email address.",
  },
  "taskCreation.auth.email.suggestion": {
    id: "taskCreation.auth.email.suggestion",
    defaultMessage: 'Did you mean "{email}"?',
  },
  "taskCreation.auth.email.suggestion.accept": {
    id: "taskCreation.auth.email.suggestion.accept",
    defaultMessage: "Yes",
  },
  "taskCreation.auth.email.suggestion.dismiss": {
    id: "taskCreation.auth.email.suggestion.dismiss",
    defaultMessage: "No",
  },
  /* `<link>` wraps the anchor half; the screen supplies the URL. */
  "taskCreation.auth.email.terms": {
    id: "taskCreation.auth.email.terms",
    defaultMessage:
      "By continuing I accept the <link>General Conditions</link> of Ring Twice",
  },

  /* Provider sign-in, offered beneath the email form. */
  "taskCreation.auth.sso.or": {
    id: "taskCreation.auth.sso.or",
    defaultMessage: "or",
  },
  "taskCreation.auth.sso.google": {
    id: "taskCreation.auth.sso.google",
    defaultMessage: "Continue with Google",
  },
  "taskCreation.auth.sso.facebook": {
    id: "taskCreation.auth.sso.facebook",
    defaultMessage: "Continue with Facebook",
  },
  "taskCreation.auth.sso.apple": {
    id: "taskCreation.auth.sso.apple",
    defaultMessage: "Continue with Apple",
  },
  "taskCreation.auth.sso.missingInfo.title": {
    id: "taskCreation.auth.sso.missingInfo.title",
    defaultMessage: "Missing info",
  },

  /* Login screen. */
  "taskCreation.auth.login.title": {
    id: "taskCreation.auth.login.title",
    defaultMessage: "Welcome back {firstName}",
  },
  "taskCreation.auth.password.label": {
    id: "taskCreation.auth.password.label",
    defaultMessage: "Password",
  },
  "taskCreation.auth.password.required": {
    id: "taskCreation.auth.password.required",
    defaultMessage: "Enter a password.",
  },
  "taskCreation.auth.password.show": {
    id: "taskCreation.auth.password.show",
    defaultMessage: "Show password",
  },
  "taskCreation.auth.password.hide": {
    id: "taskCreation.auth.password.hide",
    defaultMessage: "Hide password",
  },
  "taskCreation.auth.password.incorrect": {
    id: "taskCreation.auth.password.incorrect",
    defaultMessage: "Incorrect password.",
  },
  "taskCreation.auth.password.forgotten": {
    id: "taskCreation.auth.password.forgotten",
    defaultMessage: "Password forgotten?",
  },

  /* Registration screen. */
  "taskCreation.auth.register.title": {
    id: "taskCreation.auth.register.title",
    defaultMessage: "Complete your registration",
  },
  "taskCreation.auth.register.firstName.label": {
    id: "taskCreation.auth.register.firstName.label",
    defaultMessage: "First name",
  },
  "taskCreation.auth.register.firstName.placeholder": {
    id: "taskCreation.auth.register.firstName.placeholder",
    defaultMessage: "Eric",
  },
  "taskCreation.auth.register.firstName.required": {
    id: "taskCreation.auth.register.firstName.required",
    defaultMessage: "Enter your first name.",
  },
  "taskCreation.auth.register.firstName.invalid": {
    id: "taskCreation.auth.register.firstName.invalid",
    defaultMessage: "Enter a valid first name.",
  },
  "taskCreation.auth.register.lastName.label": {
    id: "taskCreation.auth.register.lastName.label",
    defaultMessage: "Surname",
  },
  "taskCreation.auth.register.lastName.placeholder": {
    id: "taskCreation.auth.register.lastName.placeholder",
    defaultMessage: "Cantona",
  },
  "taskCreation.auth.register.lastName.required": {
    id: "taskCreation.auth.register.lastName.required",
    defaultMessage: "Enter your last name.",
  },
  "taskCreation.auth.register.lastName.invalid": {
    id: "taskCreation.auth.register.lastName.invalid",
    defaultMessage: "Enter a valid last name.",
  },
  "taskCreation.auth.register.password.placeholder": {
    id: "taskCreation.auth.register.password.placeholder",
    defaultMessage: "Min. {min} characters",
  },
  "taskCreation.auth.register.phone.label": {
    id: "taskCreation.auth.register.phone.label",
    defaultMessage: "Phone number",
  },
  "taskCreation.auth.register.phone.invalid": {
    id: "taskCreation.auth.register.phone.invalid",
    defaultMessage: "Enter a valid phone number.",
  },
  "taskCreation.auth.register.business.explanation": {
    id: "taskCreation.auth.register.business.explanation",
    defaultMessage: "Work with providers who have a VAT number.",
  },
  "taskCreation.auth.register.newsletter": {
    id: "taskCreation.auth.register.newsletter",
    defaultMessage:
      "I would like to receive tips and special offers from Ring Twice",
  },

  /* The refusal behind `unauthorized`, shown on the password field. */
  "taskCreation.auth.error.invalidCredentials": {
    id: "taskCreation.auth.error.invalidCredentials",
    defaultMessage: "Incorrect email address or password. Try again.",
  },

  /* Auth errors a screen still words for itself. Everything else a refusal
     can be — a banned account, a rejected name, a login or SSO that failed
     for any other reason, a failed email lookup — is the flow's generic
     `error.saveFailed`: nothing the visitor could do differently. */
  /* `showAccountError` toasts a formatted string, so the support link this
     copy carries in the sheet (r431) is flattened to its words. */
  "taskCreation.auth.error.emailTaken": {
    id: "taskCreation.auth.error.emailTaken",
    defaultMessage:
      "We couldn't create your account with this email address. Contact support if you think this is a mistake.",
  },
  "taskCreation.auth.error.registerFailed": {
    id: "taskCreation.auth.error.registerFailed",
    defaultMessage:
      "Something went wrong while creating your account. Try again.",
  },

  /* Loading. */
  "taskCreation.aiLoaderModal.defaultCaption": {
    id: "taskCreation.aiLoaderModal.defaultCaption",
    defaultMessage: "Analysing your request...",
  },
  "taskCreation.profilePicsLoaderModal.defaultCaption": {
    id: "taskCreation.profilePicsLoaderModal.defaultCaption",
    defaultMessage: "Reaching out to skilled providers...",
  },

  /* Success modal, shown after a successful publish. */
  "taskCreation.successModal.title": {
    id: "taskCreation.successModal.title",
    defaultMessage: "Your request is now online!",
  },
  "taskCreation.successModal.subtext": {
    id: "taskCreation.successModal.subtext",
    defaultMessage: "Offers from providers will arrive shortly.",
  },
  "taskCreation.successModal.body": {
    id: "taskCreation.successModal.body",
    defaultMessage: "Get notified immediately by downloading our mobile app.",
  },
  "taskCreation.successModal.seeMyRequest": {
    id: "taskCreation.successModal.seeMyRequest",
    defaultMessage: "See my request",
  },
  "taskCreation.successModal.getTheApp": {
    id: "taskCreation.successModal.getTheApp",
    defaultMessage: "Get the Ring Twice app",
  },
});
